import { isObject } from 'underscore';

const DB_NAME = 'app-storage';
const DB_VERSION = 1;
const STORE_NAME = 'kv';

interface StoreItem {
  item: string;
  __expiration?: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE_NAME);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error(`IndexedDB open blocked for ${DB_NAME}`));
    });
  }
  return dbPromise;
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await getDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const req = fn(store);
    let result: T;
    req.onsuccess = () => {
      result = req.result;
    };
    req.onerror = () => reject(req.error);
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export async function setStorageItem(key: string, item: string, expirationInHours?: number): Promise<void> {
  if (isObject(key)) {
    console.error(`setStorageItem: key is an object: ${JSON.stringify(key)}`);
    return;
  }

  const storeItem: StoreItem = {
    item: item,
  };

  if (expirationInHours) {
    storeItem.__expiration = Date.now() + expirationInHours * 60 * 60 * 1000;
  }

  await tx('readwrite', (store) => store.put(storeItem, key));
}

export async function setStorageObject(
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any | { [key: string]: any },
  expirationInHours?: number
): Promise<void> {
  if (isObject(key)) {
    console.error(`setStorageObject: key is an object: ${JSON.stringify(key)}`);
    return;
  }

  if (expirationInHours) {
    item = {
      ...item,
      __expiration: Date.now() + expirationInHours * 60 * 60 * 1000,
    };
  }

  await tx('readwrite', (store) => store.put(item, key));
}

export async function setStorageArray<Type>(key: string, item: Array<Type>, expirationInHours?: number): Promise<void> {
  if (isObject(key)) {
    console.error(`setStorageArray: key is an object: ${JSON.stringify(key)}`);
    return;
  }

  const storeItem = { array: item };

  await setStorageObject(key, storeItem, expirationInHours);
}

// NOTE: this SHOULD be returning 'string | null' because storage item can ONLY be a string
// but we've got some code issues with setting a proper response type. This should be fixed separately.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getStorageItem(key: string): Promise<any | null> {
  if (isObject(key)) {
    console.error(`getStorageItem: key is an object: ${JSON.stringify(key)}`);
    return null;
  }

  const item = await tx<StoreItem | undefined>('readonly', (store) => store.get(key));

  if (item) {
    if (item.__expiration && item.__expiration < Date.now()) {
      await removeStorageItem(key);
      return null;
    } else {
      return item.item;
    }
  } else {
    return null;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getStorageObject<T = any>(key: string, newTtlHours?: number): Promise<T | null> {
  if (isObject(key)) {
    console.error(`getStorageObject: key is an object: ${JSON.stringify(key)}`);
    return null;
  }

  const item = await tx<{ __expiration?: number } | undefined>('readonly', (store) => store.get(key));

  if (item) {
    // if the ttl should be updated, just reset the object with the new ttl
    if (newTtlHours !== undefined && newTtlHours >= 0) {
      await setStorageObject(key, item, newTtlHours);
    }

    if (item.__expiration && item.__expiration < Date.now()) {
      await removeStorageItem(key);
      return null;
    } else {
      // we don't want the __expiration key included in the response
      const withoutExpKey = { ...item };

      if ('__expiration' in withoutExpKey) {
        delete withoutExpKey.__expiration;
      }

      return withoutExpKey as T;
    }
  } else {
    return null;
  }
}

export async function getStorageArray<Type>(key: string): Promise<Array<Type>> {
  if (isObject(key)) {
    console.error(`getStorageArray: key is an object: ${JSON.stringify(key)}`);
    return [];
  }

  const storedArray = await getStorageObject<{ array: Array<Type> }>(key);
  if (!storedArray || !storedArray.array || !Array.isArray(storedArray.array)) {
    return [];
  }
  return storedArray.array;
}

export async function removeStorageItem(key: string): Promise<void> {
  if (isObject(key)) {
    console.error(`removeStorageItem: key is an object: ${JSON.stringify(key)}`);
    return;
  }

  await tx('readwrite', (store) => store.delete(key));
}

export async function clearStorage(): Promise<void> {
  await tx('readwrite', (store) => store.clear());
}
